/**
 * Lewy sidebar wizarda V2 — kroki + karta „Wskazówka".
 * Pokazuje progress (zielone checkmarki dla ukończonych kroków, fioletowy ring dla aktywnego).
 */
import { Check, Sparkles } from 'lucide-react';
import { useState } from 'react';

export default function WizardSidebar({ steps, currentStep, completedSteps, onStepClick, title, sequential }) {
  const [collapsed, setCollapsed] = useState(true);
  const active = steps.find((s) => s.id === currentStep);
  const pct = Math.round((currentStep / steps.length) * 100);
  return (
    <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0" data-testid="wizard-v2-sidebar">
      {/* Mobile compact progress + toggle (lg:hidden) — formularz ma priorytet, lista zwinięta */}
      <div className="lg:hidden mb-3" data-testid="wizard-v2-mobile-progress">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="wizard-v2-sidebar-toggle"
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-white border border-orange-200 shadow-sm"
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
              {currentStep}
            </span>
            <span className="min-w-0 text-left">
              <span className="block text-[10px] uppercase tracking-wider font-semibold text-orange-600 leading-none">Krok {currentStep} z {steps.length}</span>
              <span className="block text-sm font-semibold text-gray-900 truncate leading-tight mt-0.5">{active?.name}</span>
            </span>
          </span>
          <span className="text-xs text-orange-500 font-semibold flex-shrink-0">
            {collapsed ? 'Pokaż kroki' : 'Ukryj'}
          </span>
        </button>
        <div className="mt-2 h-1.5 rounded-full bg-orange-100 overflow-hidden">
          <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className={`${collapsed ? 'hidden lg:block' : 'block'} lg:sticky lg:top-24 space-y-5`}>
        {/* Tagline */}
        <div className="hidden lg:block px-1">
          <h2 className="text-lg font-bold text-gray-900 leading-tight">
            {title ? (
              title
            ) : (
              <>
                Budujemy Twoją <span className="text-orange-500">prezentację</span>
              </>
            )}
          </h2>
        </div>

        {/* Steps */}
        <nav className="space-y-1">
          {steps.map((s, idx) => {
            const isActive = s.id === currentStep;
            const isCompleted = completedSteps.includes(s.id);
            // Tryb sekwencyjny (sequential=true): można wejść tylko na kroki już osiągnięte
            // (bieżący lub wcześniejsze) — nie wolno przeskakiwać do przodu.
            // Domyślnie (Kreator Hodowli): wolna nawigacja — bez gatingu.
            const canJump = sequential ? (s.id <= currentStep || isCompleted) : true;

            return (
              <button
                key={s.id}
                type="button"
                onClick={() => canJump && onStepClick(s.id)}
                disabled={!canJump}
                data-testid={`wizard-v2-step-nav-${s.id}`}
                className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all ${
                  isActive
                    ? 'bg-orange-50 border border-orange-200 shadow-sm'
                    : canJump
                    ? 'hover:bg-gray-50 border border-transparent'
                    : 'border border-transparent opacity-60 cursor-not-allowed'
                }`}
              >
                {/* Circle indicator */}
                <div
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    isCompleted
                      ? 'bg-emerald-500 text-white'
                      : isActive
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-200'
                      : 'border-2 border-gray-300 text-gray-400'
                  }`}
                >
                  {isCompleted ? <Check className="w-4 h-4" /> : s.id}
                </div>

                {/* Label + subtitle */}
                <div className="min-w-0 flex-1 pt-0.5">
                  <p
                    className={`text-sm font-semibold leading-tight ${
                      isActive
                        ? 'text-orange-900'
                        : isCompleted
                        ? 'text-gray-900'
                        : 'text-gray-700'
                    }`}
                  >
                    {s.name}
                  </p>
                  {s.subtitle && (
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">{s.subtitle}</p>
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        {/* Wskazówka card */}
        <div
          className="rounded-2xl bg-gradient-to-br from-orange-500/10 via-orange-400/10 to-orange-400/10 border border-orange-200/60 p-4"
          data-testid="wizard-v2-ai-info-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-orange-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm font-bold text-orange-900">Wskazówka</p>
          </div>
          <p className="text-xs text-gray-700 leading-relaxed">
            Uzupełnij dane krok po kroku — podgląd po prawej aktualizuje się na żywo.
          </p>
        </div>
      </div>
    </aside>
  );
}
